// SecretsPage.jsx — 第一百零九批 D：定向密递的**取件面**（页面侧此前零文件）。
//
// ── 这一页不是顺带补的，它是一条死锁的解 ────────────────────────────────────
// 后端的四只密递端点从第一百零六批就在，全是 require_session ⇒ 浏览器一直调得动；
// 缺的一直只是页面。而 CLI 要一枚 key 才能跑、key 只能从密递取、密递此前只能用 CLI
// 取 ⇒ **「用 key 才能取到 key」**。裁定 1（API key 延迟领取 = 复用定向密递）整条路
// 能不能成立，就取决于这一页与 /cli 下载页两块都到位。
//
// ── 取件是全站唯一一处**破坏性的读**，所以有三条硬规矩 ──────────────────────
//  ① **不自动取件**。进页面只列清单（清单端点永不含正文），正文只在明确点击并过了
//     确认之后才取。一个"自动加载详情"的页面会在按 F5 时把一件 once 密件烧掉。
//     判据：secrets.test.mjs 读本文件的加载段，禁掉其中的取件调用。
//  ② **确认对话里要说清是哪一种**。一次性件说"取出就烧掉、这个窗口关掉也没了"，
//     限时件说"到 {t} 之前还能再取"。给同一句话就是把两件后果完全不同的事说成一样。
//  ③ 正文只进 readonly 的多行输入框 + 复制按钮：**绝不进地址栏、绝不进浏览器本地
//     存储、绝不进任何控制台输出**。判据同样读源码钉住（它按那几个 API 的名字扫，
//     所以这段注释刻意不写它们的字面名——判据不该被自己的说明文字绊倒）。
//
// ── once + ttl（本批解禁的组合）────────────────────────────────────────────
// 密递头注原本否决"既限时又即焚"，理由是「取件方分不清撞的是哪一种失败」。
// GONE_TAKEN / GONE_EXPIRED / GONE_REVOKED 三码 + 三句人话上线之后那条理由已经不成立
// ——规则的适用范围由它的理由划定。本页因此要能表达第三种形态（secrets.mode.onceTtl），
// 并把三种失败各自的人话打出来：被取走 ⇒ 去轮换那枚凭据（不是请人重发），
// 过期/吊销 ⇒ 找签发方。搞反了代价极不对称。
import { useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Stack, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AgentChip from '../components/AgentChip.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  SECRET_ERROR_KEYS, downloadSpec, mayRevoke, modeText, normalizeDeliveries, stateColor,
  stateLabelKey, takeAction, takeConfirmKey,
} from './secrets.mjs';

export default function SecretsPage() {
  const t = useT();
  const { lang } = useLang();
  const { account } = useAuth();
  const { snack, notify, clearSnack } = useNotify();

  // 清单端点**永不含正文**（后端的行结构里就没有那个字段）。这一整段是页面唯一的
  // 自动加载路径，取件绝不能出现在这里——按一次 F5 就会烧掉一件 once 密件。
  const list = useLoad(() => api.secretDeliveries(), []);
  // AUTO-TAKE GUARD END —— 判据读到这里为止（secrets.test.mjs）。
  // ── SB-159（第一百七十八批）：加载更多（键集游标，MessagesPage 同款）────────
  // 追加页单独存：useLoad 是一次性加载器，没有分页概念。本表刻意永不删行，
  // 首页之外的行此前对页面根本不存在——而且没有任何东西说出这一点。
  const [more, setMore] = useState([]);
  const [moreState, setMoreState] = useState({ loading: false, err: null });
  const pageRows = normalizeDeliveries(list.data);
  const rows = [...pageRows, ...more];
  const lastCursor = more.length
    ? more[more.length - 1]?.id : (pageRows.at(-1)?.id ?? null);
  const hasMore = more.length
    ? moreState.hasMore === true : list.data?.hasMore === true;
  const loadMore = async () => {
    if (!lastCursor || moreState.loading) return;
    setMoreState({ loading: true, err: null });
    try {
      const d = await api.secretDeliveries({ before: lastCursor });
      setMore((prev) => [...prev, ...normalizeDeliveries(d)]);
      setMoreState({ loading: false, err: null, hasMore: d?.hasMore === true });
    } catch (e) {
      setMoreState({ loading: false, err: e, hasMore: true });
    }
  };

  const [confirmRow, setConfirmRow] = useState(null);
  const [revealed, setRevealed] = useState(null);   // { row, body } —— 只活在内存里
  const [busy, setBusy] = useState(false);

  const errText = (e, fallback) => t(errKey(e, { byCode: SECRET_ERROR_KEYS, fallback }));

  const doTake = async () => {
    const row = confirmRow;
    if (!row) return;
    setBusy(true);
    setConfirmRow(null);
    try {
      const r = await api.takeSecretDelivery(row.id);
      // 正文只落进这一个组件状态：不写浏览器本地存储、不进地址栏、不打日志。
      setRevealed({ row, body: typeof r?.body === 'string' ? r.body : '' });
      list.reload();
    } catch (e) {
      notify(errText(e, 'secrets.error'), 'error');
      list.reload();
    } finally {
      setBusy(false);
    }
  };

  const doRevoke = async (row) => {
    setBusy(true);
    try {
      await api.revokeSecretDelivery(row.id);
      notify(t('secrets.revoke.ok'), 'success');
      list.reload();
    } catch (e) {
      notify(errText(e, 'secrets.error'), 'error');
      list.reload();
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(revealed.body);
      notify(t('secrets.body.copied'), 'success');
    } catch {
      // 剪贴板不可用（非安全上下文/权限）——正文就在框里，让他自己选中复制。
      // 这里刻意不回落到任何"写到别处再复制"的花招：那会给正文多造一个副本。
    }
  };

  // ── 下载为 .txt（SB-148 A）────────────────────────────────────────────────
  // Blob 当场从内存里那一份生成：**不重取**（一次性件库里那份可能已经烧了，重取
  // 只会拿到 410）、不缓存到组件外。内容与文件名由 downloadSpec 一处定形——
  // 逐字节 = 值本身，判据在 secrets.test.mjs 里咬住。
  const onDownload = () => {
    const spec = downloadSpec(revealed.row, revealed.body);
    const blob = new Blob(spec.parts, { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = spec.name;
    // 挂进文档再点：不在文档里的锚点，个别浏览器不触发下载。
    document.body.appendChild(a);
    a.click();
    a.remove();
    // 撤销推迟一拍：同一任务里撤销，浏览器可能还没来得及按这个 URL 去取 Blob。
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const modeChip = (row) => {
    const m = modeText(row);
    if (!m) return <Chip size="small" variant="outlined" label={row.mode || '—'} />;
    return (
      <Chip
        size="small"
        variant="outlined"
        color={m.key === 'secrets.mode.onceTtl' || m.key === 'secrets.mode.once' ? 'warning' : 'default'}
        label={m.ms == null ? t(m.key) : t(m.key, { t: fmtTime(m.ms, lang) })}
      />
    );
  };

  const confirm = confirmRow ? takeConfirmKey(confirmRow) : null;

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('secrets.title')}
        <HelpLink section="secrets" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('secrets.subtitle')}
      </Typography>

      {list.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(list.error, { fallback: 'secrets.error' }))}
          </Alert>
          <Button size="small" onClick={list.reload}>{t('common.retry')}</Button>
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('secrets.empty')}</Typography>
        </Paper>
      ) : (
        <DataTable minWidth={860}>
          <TableHead>
            <TableRow>
              <TableCell>{t('secrets.col.label')}</TableCell>
              <TableCell>{t('secrets.col.from')}</TableCell>
              <TableCell>{t('secrets.col.to')}</TableCell>
              <TableCell>{t('secrets.col.mode')}</TableCell>
              <TableCell>{t('secrets.col.state')}</TableCell>
              <TableCell>{t('secrets.col.created')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {row.label || `#${row.id}`}
                  </Typography>
                  {row.mine ? (
                    <Chip size="small" variant="outlined" label={t('secrets.mine')}
                          sx={{ mt: 0.5 }} />
                  ) : null}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    {/* SB-147：显示名旁并排 #id（成员面不露整串邮箱——后端
                        person_display 口径；#id 是稳定身份位，同名两人凭它分开）。 */}
                    <Typography variant="body2">
                      {row.fromDisplay || '—'}
                      {row.fromId != null ? ` #${row.fromId}` : ''}
                    </Typography>
                    {row.viaAgent ? <AgentChip /> : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.toAccount != null
                      ? `${row.toAccount}${row.toAccountId != null ? ` #${row.toAccountId}` : ''}`
                      : (row.toGroup ?? '—')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {row.toGroup}
                  </Typography>
                </TableCell>
                <TableCell>{modeChip(row)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={stateColor(row.state)}
                    label={stateLabelKey(row.state) ? t(stateLabelKey(row.state)) : (row.state || '—')}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {fmtTime(row.createdAtMs, lang)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    <TakeCell row={row} account={account} busy={busy} t={t}
                              onTake={() => setConfirmRow(row)} />
                    {mayRevoke(row, account) ? (
                      <Button size="small" color="error" disabled={busy}
                              onClick={() => doRevoke(row)}>
                        {t('secrets.revoke')}
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      )}

      {/* ── 加载更多（SB-159）：只在服务端说还有更多时出现；取回失败时按钮留在
          原地并出声——"取不到"绝不能长得像"没有了"（MessagesPage 同款三句）。 ── */}
      {hasMore ? (
        <Stack sx={{ mt: 2, alignItems: 'center' }}>
          <Button size="small" onClick={loadMore} disabled={moreState.loading}>
            {moreState.loading ? t('secrets.loadMoreBusy') : t('secrets.loadMore')}
          </Button>
          {moreState.err ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {t('secrets.loadMoreError')}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {t('secrets.revoke.desc')}
      </Typography>

      {/* ── 取件确认：全站唯一一处不可撤销的**读** ────────────────────────
          **标题与正文都按 mode 分岔**（takeConfirmKey）：一次性件与限时件的后果完全
          不同。标题此前写死成 once 那一句，于是限时件的对话框标题说"取出就烧掉"、
          正文说"到期之前可以反复取出"——他点了取出、回来发现还能再取，从此不信这个
          标题，而下次真是 once 件时就闭眼点过去了。 */}
      <Dialog open={Boolean(confirmRow)} onClose={() => setConfirmRow(null)}>
        <DialogTitle>
          {confirm && confirm.title === 'secrets.take.confirmTitleTtl'
            ? t('secrets.take.confirmTitleTtl') : t('secrets.take.confirmTitleOnce')}
          {/* SB-151 控件级 ❓：取件即焚/两形态的完整语义在手册密件章。此刻还没取件，
              导航走零副作用——正文仍在库里。 */}
          <HelpLink section="secrets" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm && confirm.body === 'secrets.take.confirmBodyTtl'
              ? t(confirm.body, { t: fmtTime(confirm.ms, lang) })
              : t('secrets.take.confirmBodyOnce')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={doTake}>
            {t('secrets.take.confirmOk')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 正文：只在这里存在一次 ──────────────────────────────────────
          readonly 多行框 + 复制按钮。关掉这个对话框，这段文字就在世上消失了
          （once 件的库里那一份已经烧掉），所以警告文案与关闭按钮同框。 */}
      <Dialog open={Boolean(revealed)} onClose={() => setRevealed(null)} fullWidth maxWidth="sm">
        {/* SB-151 控件级 ❓ 放**标题行**而不是动作行（证伪评审：动作行里它紧贴「关闭」，
            偏一指就点掉全对话框里唯一不可逆的按钮——once 正文永久消失；且 DialogActions
            不折行，320px 屏四件会挤爆）。**必须 newTab**：这只窗口是一次性密件正文在
            世上唯一的存身处，站内导航会卸载它——新标签页读手册，这里一动不动。 */}
        <DialogTitle>
          {t('secrets.body.label')}
          <HelpLink section="secrets" newTab />
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('secrets.body.warning')}</Alert>
          <TextField
            value={revealed?.body ?? ''}
            multiline
            minRows={4}
            fullWidth
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'ui-monospace, monospace' } } }}
          />
          {/* 两条出口（复制/下载）各自的泄漏面要在**选之前**说清——两通道并存，
              不替用户做选择（SB-148 §1A）。 */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('secrets.download.note')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCopy}>{t('secrets.body.copy')}</Button>
          <Button onClick={onDownload}>{t('secrets.download')}</Button>
          <Button variant="contained" onClick={() => setRevealed(null)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// 取件那一格。三态由 secrets.mjs 的 takeAction 派生：
//  · take       —— 按钮
//  · issuerOnly —— 一句解释而不是一个会吃 403 的按钮（他手上本就有原件，点一下只会
//                  把一件 once 密件白白烧掉，收件方随后拿到 410 还以为被人截走了）
//  · none       —— 终局，什么都不渲染
function TakeCell({ row, account, busy, t, onTake }) {
  const action = takeAction(row, account);
  if (action === 'none') return null;
  if (action === 'issuerOnly') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 220 }}>
        {t('secrets.err.issuerCannotTake')}
      </Typography>
    );
  }
  return (
    <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={onTake}>
      {t('secrets.take')}
    </Button>
  );
}
